import { useMemo, useState } from "react";
import { ShieldCheck, SignIn, WarningCircle } from "@phosphor-icons/react";

const ACTION_LABELS = {
  login: "Login",
  login_failed: "Failed login",
  access_granted: "Access granted",
  roster_copied: "Roster copied",
  user_created: "User created",
  user_updated: "User updated",
  user_removed: "User removed",
  password_reset: "Password reset",
  event_created: "Event logged",
  event_updated: "Event updated",
  event_deleted: "Event deleted",
};

function actionLabel(action) {
  return ACTION_LABELS[action] || action.replace(/_/g, " ");
}

function entryIcon(action) {
  if (action === "login") return <SignIn size={16} weight="bold" className="text-[#32D74B] shrink-0 mt-0.5" />;
  if (action === "login_failed") return <WarningCircle size={16} weight="bold" className="text-[#FF3B30] shrink-0 mt-0.5" />;
  return <ShieldCheck size={16} weight="bold" className="text-[#CCFF00] shrink-0 mt-0.5" />;
}

// The Audit log tab on Admin — now with login/failed-login tracking plus
// simple user + event-type filters (filtering is client-side since the
// list is already fully fetched, same approach as the rest of Admin).
export function AuditLogList({ auditLog, formatDate }) {
  const [userFilter, setUserFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  const users = useMemo(() => {
    return Array.from(new Set(auditLog.map((e) => e.actor_name).filter(Boolean))).sort();
  }, [auditLog]);

  const types = useMemo(() => {
    return Array.from(new Set(auditLog.map((e) => e.action)));
  }, [auditLog]);

  const filtered = auditLog.filter((entry) => {
    if (userFilter !== "all" && entry.actor_name !== userFilter) return false;
    if (typeFilter !== "all" && entry.action !== typeFilter) return false;
    return true;
  });

  return (
    <div className="space-y-3" data-testid="audit-log-list">
      {auditLog.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="audit-log-filters">
          <select
            value={userFilter}
            onChange={(e) => setUserFilter(e.target.value)}
            data-testid="audit-log-filter-user"
            className="bg-[#0A0A0A] border border-zinc-800 text-white text-xs px-2 py-1.5 rounded-sm focus:outline-none focus:border-[#CCFF00] w-fit"
          >
            <option value="all">All users</option>
            {users.map((u) => (
              <option key={u} value={u}>{u}</option>
            ))}
          </select>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            data-testid="audit-log-filter-type"
            className="bg-[#0A0A0A] border border-zinc-800 text-white text-xs px-2 py-1.5 rounded-sm focus:outline-none focus:border-[#CCFF00] w-fit"
          >
            <option value="all">All activity</option>
            {types.map((t) => (
              <option key={t} value={t}>{actionLabel(t)}</option>
            ))}
          </select>
        </div>
      )}
      {filtered.length === 0 ? (
        <p className="text-zinc-600 text-sm" data-testid="audit-log-empty">
          {auditLog.length === 0 ? "No admin activity logged yet." : "No activity matches these filters."}
        </p>
      ) : (
        filtered.map((entry) => (
          <div key={entry.id} data-testid={`audit-row-${entry.id}`} className="flex items-start gap-3 bg-[#121212] border border-zinc-800 rounded-sm px-4 py-3">
            {entryIcon(entry.action)}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                {(entry.action === "login" || entry.action === "login_failed") && (
                  <span
                    data-testid={`audit-badge-${entry.id}`}
                    className={`font-label text-[10px] uppercase px-2 py-0.5 rounded-sm w-fit ${
                      entry.action === "login" ? "text-[#32D74B] bg-[#32D74B]/10" : "text-[#FF3B30] bg-[#FF3B30]/10"
                    }`}
                  >
                    {actionLabel(entry.action)}
                  </span>
                )}
                <p className="text-sm text-white">{entry.details}</p>
              </div>
              <p className="text-xs text-zinc-500 mt-0.5">
                {entry.actor_name}{entry.actor_role ? ` (${entry.actor_role})` : ""} · {formatDate(entry.created_at)}
              </p>
            </div>
          </div>
        ))
      )}
    </div>
  );
}
